import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticles, getCategories } from '../services/api';
import { ChevronRight } from 'lucide-react';
import SectionHeader from '../components/news/SectionHeader';
import QuickEditButton from '../components/news/QuickEditButton';

const FB = 'https://images.unsplash.com/photo-1596796679119-7cf1a9e5448b?q=80&w=800';
const CAT_FALLBACKS = {
  cat_politics: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=800',
  cat_business: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800',
  cat_sports: 'https://images.unsplash.com/photo-1461896836934-bd45ea8dbc33?q=80&w=800',
  cat_entertainment: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=800',
  cat_technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800',
  cat_culture: 'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?q=80&w=800',
  cat_local: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e13?q=80&w=800',
};
const getFB = (catId) => CAT_FALLBACKS[catId] || FB;
const oE = (e, catId) => { const fb = getFB(catId); if (e.target.src !== fb) e.target.src = fb; };
const CC = { cat_politics:'#d32f2f', cat_business:'#1565c0', cat_sports:'#2e7d32', cat_entertainment:'#7b1fa2', cat_technology:'#0277bd', cat_culture:'#e65100', cat_local:'#00695c' };

function ago(d, kn) {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return kn ? `${m} ನಿಮಿಷಗಳ ಹಿಂದೆ` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return kn ? `${h} ಗಂಟೆಗಳ ಹಿಂದೆ` : `${h}h ago`;
  return kn ? `${Math.floor(h/24)} ದಿನಗಳ ಹಿಂದೆ` : `${Math.floor(h/24)}d ago`;
}

function filterByTab(articles, tab) {
  const safe = (articles || []).filter(a => a?.article_id);
  if (tab === 'featured') { const f = safe.filter(a => a.featured); return f.length ? f : safe; }
  if (tab === 'trending') return safe.slice().sort((a,b) => (b.views||0) - (a.views||0));
  return safe.slice().sort((a,b) => new Date(b.published_at||0) - new Date(a.published_at||0));
}

export default function CategoryPage() {
  const { id } = useParams();
  const { isKannada } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('latest');

  useEffect(() => {
    setLoading(true);
    Promise.all([getArticles({ category: id, page, limit: 12 }), getCategories()])
      .then(([artR, catR]) => {
        setArticles(artR.data.articles || []);
        setTotal(artR.data.total || 0);
        setCategory((catR.data || []).find(c => c.category_id === id));
      }).catch(console.error).finally(() => setLoading(false));
  }, [id, page]);

  const name = isKannada ? (category?.name_kn || category?.name || '') : (category?.name || '');
  const accent = CC[id] || '#d32f2f';
  const gT = (a) => (isKannada ? (a.title_kn || a.title) : a.title) || '';
  const gS = (a) => (isKannada ? (a.summary_kn || a.summary) : a.summary) || '';
  const catName = category ? (isKannada ? (category.name_kn || category.name) : category.name) : '';

  const filtered = useMemo(() => filterByTab(articles, tab), [articles, tab]);

  return (
    <div className="bg-[#f8f8f8] min-h-screen" data-testid="category-page">
      <div className="max-w-[1400px] mx-auto px-4 py-5">
        <nav className="flex items-center gap-1.5 text-[12px] text-gray-500 mb-4">
          <Link to="/" className="hover:text-red-600">{isKannada ? 'ಮುಖಪುಟ' : 'Home'}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 font-bold" data-testid="category-title">{name}</span>
        </nav>

        <SectionHeader
          title={name}
          catId={id}
          accent={accent}
          activeTab={tab}
          onTabChange={setTab}
          showMore={false}
        />

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">{[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-xl animate-pulse"><div className="aspect-video bg-gray-200 rounded-t-xl" /><div className="p-4 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-3 bg-gray-200 rounded w-1/2" /></div></div>)}</div>
        ) : articles.length === 0 ? (
          <p className="text-center text-gray-500 py-16">{isKannada ? 'ಲೇಖನಗಳಿಲ್ಲ.' : 'No articles yet.'}</p>
        ) : (
          <>
            {/* Grid of articles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
              {filtered.map(a => (
                <div key={a.article_id} className="relative group">
                  <QuickEditButton articleId={a.article_id} />
                  <Link
                    to={`/article/${a.slug || a.article_id}`}
                    className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-[0_10px_28px_-14px_rgba(0,0,0,0.22)] transition-all flex flex-col block"
                    data-testid={`category-card-${a.article_id}`}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img src={a.featured_image || getFB(id)} alt="" className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500" onError={(e) => oE(e, id)} />
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold text-white rounded" style={{ backgroundColor: accent }}>{catName}</span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-[15px] font-bold text-[#1a1a1a] line-clamp-2 font-kannada group-hover:text-[color:var(--a)]" style={{ '--a': accent }}>{gT(a)}</h3>
                      {gS(a) && <p className="text-[12.5px] text-gray-500 line-clamp-2 mt-1.5 leading-relaxed">{gS(a)}</p>}
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {Math.ceil(total / 12) > 1 && (
              <div className="flex justify-center gap-1.5 mt-4">
                {Array.from({ length: Math.min(Math.ceil(total / 12), 10) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded text-[12px] font-bold transition-all ${p === page ? 'text-white shadow' : 'bg-white border border-gray-200 text-gray-600 hover:border-[color:var(--a)]'}`}
                    style={p === page ? { backgroundColor: accent } : { '--a': accent }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
